/* eslint react/jsx-props-no-spreading: 0 */
import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useRef,
} from 'react';

import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import cn from 'classnames';
import {
  random,
  debounce,
  isEmpty,
  isFunction,
  isNil,
  isObject,
  isString,
  noop,
  uniqBy,
  isBoolean,
} from 'lodash';
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';

import Spinner from 'react-spinkit';

import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';

import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputAdornment from '@mui/material/InputAdornment';
import ListSubheader from '@mui/material/ListSubheader';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';

import Button from 'components/Common/Buttons/ButtonV3';
import Chip from 'components/Common/Chip';
import ClickableComponent from 'components/Common/ClickableComponent';
import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import ListBoxAdapterComponent from 'components/Common/Select/components/ListBoxAdapterComponent';
import SelectWithNodeLabel from 'components/Common/Select/components/SelectWithNodeLabel';
import Text from 'components/Common/Text';
import TextLinkButton from 'components/Common/TextLinkButton';
import Tooltip from 'components/Common/Tooltip';
import { debounceInterval } from 'constants/debounceInterval';
import usePaginatedApi from 'hooks/usePaginatedApi';
import { ensureArray, isNonEmptyArray } from 'utils/arrays';
import { ensureObject } from 'utils/objects';

import s from './Select.css';

const filter = createFilterOptions();

const renderGroup = params => {
  const groupItem = params.group ? (
    <ListSubheader key={params.key} component="div" className={s.optionGroup}>
      {params.group}
    </ListSubheader>
  ) : null;
  return [groupItem, params.children];
};

const Menu = ({ onSelectAll, onSelectNone }) => (
  <Flexbox
    justifyContent="flex-end"
    p={1.5}
    onMouseDown={e => e.preventDefault()}
  >
    <Text variant="tiny" color="gray">
      SELECT:&nbsp;&nbsp;
    </Text>
    <TextLinkButton variant="tiny" onMouseDown={onSelectAll}>
      ALL
    </TextLinkButton>
    <Text variant="tiny" color="gray">
      &nbsp;&nbsp;|&nbsp;&nbsp;
    </Text>
    <TextLinkButton variant="tiny" onMouseDown={onSelectNone}>
      NONE
    </TextLinkButton>
  </Flexbox>
);

const shouldDisplay = ({
  disabled,
  multiple,
  controlledValue,
  displayPlaceholder,
}) => {
  const isMultipleAndEmpty = multiple && !isNonEmptyArray(controlledValue);
  const isNotMultipleAndControlled = !multiple && !controlledValue;
  const shouldDisplayForMultiple =
    !disabled && (isMultipleAndEmpty || isNotMultipleAndControlled);

  return displayPlaceholder || shouldDisplayForMultiple;
};

/**
 * A text input enhanced by a panel of suggested options
 *
 * @param apiFunc - the API function to pass into usePaginatedApi if the options should come from an API request
 * @param apiFuncName - corresponding usePaginatedApi function name, for query invalidation purposes
 * @param apiFuncOptions - any options to pass into usePaginatedApi
 * @param additionalStaticOptions - if you want to pass additional options while fetching them from api
 * @param apiFuncUniqBy - function to uniq by options, as per https://lodash.com/docs/4.17.15#uniqBy
 * @param autofill - enables autocomplete, helps users to fill forms faster, especially on mobile devices. https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
 * @param autoHighlight - If true, the first option is automatically highlighted
 * @param autoSuggestHighlight - highlights option suggestions as you type
 * @param charLimit - maximum number of characters user can enter
 * @param classes - object with classes to pass to the component
 * @param closeOnSelect - optional override to close listbox on select when multiple is true
 * @param compact - if true, the spacing and chips are compact
 * @param creatable - if true, can create new options from the input
 * @param addOptionPromptActionFn - function to execute when a user clicks add option button in creatable variant
 * @param disableClearable - if true, removes the clear button from the input
 * @param disabled - if true, field is disabled
 * @param disablePortal - if true, the Popper content will be under the DOM hierarchy of the parent component
 * @param endAdornment - end InputAdornment for this component
 * @param error - error message for the component
 * @param getAutocompleteOptionLabel - function that will populate option label when new options are fetched from api
 * @param showOptionsOnCondition - used to determine if search results are shown by search text that user entered
 * @param getOptionLabel - used to determine the string value for a given option. It's used to fill the input (and the list box options if renderOption is not provided)
 * @param getOptionNodeLabel - when passed, "SelectWithNodeLabel" component is used to provide a single select with customized selected option label, since the MUI autocomplete currently do now provide this behavior for single select
 * @param getOptionSelected - used to determine if the option represents the given value. Uses strict equality by default
 * @param glueText - optional text to render between Chips when multiple is true
 * @param groupBy - if provided, the options will be grouped under the returned string. The groupBy value is also used as the text for group headings when renderGroup is not provided
 * @param hasSelectMenu - if true, has the select all/none menu
 * @param helperText - text that shows below the input field
 * @param id - HTML id passed to the component
 * @param inputClasses - classes object for the input field
 * @param isObjectValue - if true, values are an object so type and blur doesn't change field
 * @param label - label text for the input field
 * @param labelVariant - outlined is a notched section in the top of the input; standard is next to the field
 * @param limitTags - the number of tags shown in the component; the rest are in "+ N more" link
 * @param listboxComponent - the component used to render the listbox
 * @param multiple - if true, can select multiple options
 * @param onChange - function to run when value changes
 * @param onSearchTextChange - function to run when search text value changes
 * @param open - if true, the select menu is open
 * @param openOnFocus - if true, the popup will open on input focus
 * @param options - array of options
 * @param placeholder - placeholder for input field
 * @param readOnlyInput - if true, the input field is read only
 * @param renderOption - function to render the option, use getOptionLabel by default
 * @param requireSelectedOption - if true, there must be able least one option selected
 * @param showAllOptions - if true, dropdown will render all available options without filtering. Added specifically for Google Places functionality.
 * @param startAdornment - start InputAdornment for this component
 * @param testId - ID for data-testid
 * @param inputAriaLabel - aria-label of the input element
 * @param useTitleText - if true, options will render with h6 variant text
 * @param value - the value of the autocomplete. The value must have reference equality with the option in order to be selected. You can customize the equality behavior with the isOptionEqualToValue prop.
 * @param variant - whether the component is a select or dropdown
 * @param width - optional width in px (provided as a number)
 * @param setValueAfterCreate - bool that handles if the value will be selected after create option click
 * @param optionVariableName - name of each option's variable that is shown in the dropdown. If the input value matches an option's variable name value, then we do not render the "Add <input value>" row.
 * @param onOpen - callback fired when the popup requests to be opened. Use in controlled mode
 * @param onClose - callback fired when the popup requests to be closed. Use in controlled mode
 * @param noOptionsText - Text or ReactNode to display when there are no options
 * @param showApplyButton - render Cancel and Apply button below option list
 * @param onApply - function to call when Apply button is clicked below the option list
 * @param onClearAllValues - function to call when Clear All button is clicked below the option list
 * @param onCancel - function to call when Cancel button is clicked below the option list
 * @param useVariableSizeList - If true, ListBoxAdapterComponent will use VariableSizeList (dynamic list height)
 */
// eslint-disable-next-line complexity
const Select = ({
  apiFunc,
  apiFuncName,
  apiFuncOptions,
  apiFuncUniqBy,
  additionalStaticOptions,
  autofill,
  autoHighlight,
  autoSuggestHighlight,
  charLimit,
  classes,
  closeOnSelect,
  compact,
  creatable,
  addOptionPromptActionFn,
  disableClearable,
  disabled,
  disablePortal,
  endAdornment,
  error,
  getAutocompleteOptionLabel,
  getOptionLabel,
  getOptionNodeLabel,
  getOptionSelected,
  glueText,
  groupBy,
  hasSelectMenu,
  helperText,
  id,
  inputClasses,
  isObjectValue,
  label,
  labelVariant,
  limitTags,
  listboxComponent,
  multiple,
  onChange,
  onSearchTextChange,
  open,
  openOnFocus,
  options,
  placeholder,
  readOnlyInput,
  renderOption,
  requireSelectedOption,
  showAllOptions,
  startAdornment,
  testId,
  inputAriaLabel,
  showOptionsOnCondition,
  getOptionDisabled,
  useTitleText,
  value,
  variant,
  width,
  setValueAfterCreate,
  optionVariableName,
  displayPlaceholder,
  onOpen,
  onClose,
  noOptionsText,
  chipPopoverProps,
  getChipTooltipMessage,
  showApplyButton,
  onApply,
  onCancel,
  onClearAllValues,
  useVariableSizeList,
  onBlur,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [controlledValue, setControlledValue] = useState(value);
  const [controlledOpen, setControlledOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [tagsOnDeleteFunc, setTagsOnDeleteFunc] = useState(() => () => {});
  const openPopover = ({ currentTarget }) => setAnchorEl(currentTarget);
  const closePopover = () => setAnchorEl(null);
  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen ? 'popover' : undefined;
  const lastScrollRef = useRef(0);
  const listClassName = useRef(`select-list-${random(1000, 9999)}`);

  // Unnecessary re-renders will cause list box to jump to top after scrolling to bottom
  const listBox = useMemo(
    () =>
      forwardRef((props, ref) => {
        if (listboxComponent) {
          const ListBoxComponent = listboxComponent;

          return <ListBoxComponent ref={ref} {...props} />;
        }

        return (
          <ListBoxAdapterComponent
            ref={ref}
            onApply={onApply}
            onCancel={onCancel}
            showApplyButton={showApplyButton}
            onClearAllValues={onClearAllValues}
            useVariableSizeList={useVariableSizeList}
            {...props}
          />
        );
      }),
    [
      listboxComponent,
      onApply,
      onCancel,
      onClearAllValues,
      showApplyButton,
      useVariableSizeList,
    ],
  );

  useEffect(() => {
    // query string doesn't update if we're adding new created option, and select is still
    // trying to find an option that doesn't exist in our list and shows "no options"
    // so we need to drop old query string to receive original options list and be able to select another option

    /* 
      Reason of checking if value is object is, if we set a controlled state of a "string" value, 
      which means user has clicked outside without selecting any of the options. 
      In that case we shouldn't trigger state update. 
    */
    if (!isObjectValue || isObject(value)) {
      setControlledValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, setControlledValue]);

  // Sets initial inputValue and updates on parent value change so that any implementations that use string values/options (like the state input for addresses) will populate on load
  useEffect(() => {
    if (!isString(value)) {
      return;
    }

    setInputValue(value);
  }, [value]);

  const isSelect = variant === 'select';
  const isLabelOutlined = labelVariant === 'outlined';
  const isAsync = Boolean(!isNonEmptyArray(options) && apiFunc);
  const hideLabel = compact || !isLabelOutlined;
  // ignores open if it is undefined
  const isOpen = isBoolean(open) ? open : controlledOpen;
  const { items, isLoading, loadNextPage, hasNextPage, setSearchString } =
    usePaginatedApi({
      apiFunc,
      apiFuncName,
      enabled: isAsync,
      ...apiFuncOptions,
    });
  const disabledOptionRemoval =
    requireSelectedOption && controlledValue?.length === 1;

  const onValueChange = (event, newValue) => {
    if (multiple && !newValue.length && disabledOptionRemoval) {
      return null;
    }
    if (!creatable || setValueAfterCreate) {
      return onChange(newValue);
    }
    if (!multiple) {
      if (newValue?.inputValue || isString(newValue)) {
        // inputValue key only exists if creatable is enabled and the user triggers the Add button.
        // There is a possibility for the user to press enter before the Add option appears which will return newValue as a primitive string.
        const val = newValue?.inputValue ? newValue?.inputValue : newValue;
        return onChange({ title: val, isNew: true });
      }
      return onChange(newValue);
    }

    const values = [...ensureArray(newValue)];
    const newOption = values.pop();

    if (newOption?.inputValue) {
      return onChange([
        ...values,
        { title: newOption?.inputValue, isNew: true },
      ]);
    }
    return onChange([...values, newOption]);
  };

  const filterOptions = (optionList, params) => {
    const filtered =
      isAsync || showAllOptions ? optionList : filter(optionList, params);

    // Suggest the creation of a new value as long as the suggestion isn't an exact match, including casing
    if (
      creatable &&
      params.inputValue !== '' &&
      params.inputValue !== filtered[0]?.[optionVariableName]
    ) {
      filtered.push({
        inputValue: params.inputValue,
        title: `Add "${params.inputValue}"`,
        isAddOptionPrompt: true,
        addOptionPromptActionFn,
      });
    }

    if (isLoading) {
      filtered.push({ isLoadingIndicator: true });
    }

    return filtered;
  };

  const getOptionText = option => {
    if (creatable && option.inputValue) {
      return option.inputValue;
    }
    if (creatable && option.title) {
      return option.title;
    }
    if (creatable && isString(option)) {
      return option;
    }
    return getOptionLabel ? getOptionLabel(option) : option;
  };

  const renderCheckbox = ({ selected, option }) => {
    const isDisabled = isFunction(getOptionDisabled)
      ? getOptionDisabled(option)
      : disabledOptionRemoval || disabled;

    return (
      <Checkbox
        checked={selected}
        className={s.checkbox}
        classes={{
          disabled: s.disabledCheckbox,
        }}
        disabled={selected && isDisabled}
      />
    );
  };

  const renderOptionRow = (renderProps, option, { selected }) => {
    const { isAddOptionPrompt, isLoadingIndicator } = option;

    const matches = match(option.title, inputValue);
    const parts = parse(option.title, matches);

    if (isLoadingIndicator) {
      return (
        <div {...renderProps} className={s.loadingContainer}>
          <Spinner name="ball-beat" fadeIn="none" className={s.loading} />
        </div>
      );
    }

    if (isAddOptionPrompt) {
      const renderPropsClone = {
        ...renderProps,
        onClick: isFunction(addOptionPromptActionFn)
          ? addOptionPromptActionFn
          : renderProps?.onClick,
      };
      return (
        <ClickableComponent {...renderPropsClone} className={s.newOption}>
          <FontAwesomeIcon icon={faPlus} />
          <span>{option.title}</span>
        </ClickableComponent>
      );
    }

    if (renderOption) {
      return React.cloneElement(
        <div>
          {multiple && renderCheckbox({ selected, option })}
          {renderOption(option)}
        </div>,
        {
          ...renderProps,
          className: cn(renderProps?.className, classes?.optionRow),
        },
      );
    }

    if (autoSuggestHighlight)
      return (
        <div {...renderProps}>
          {multiple && renderCheckbox({ selected, option })}
          {parts.map((part, index) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={cn(s.autoSuggestText, {
                [s.autoSuggestPart]: part.highlight,
                [s.grouped]: !!groupBy,
              })}
            >
              {part.text}
            </span>
          ))}
        </div>
      );

    return (
      <span className={cn({ [s.grouped]: !!groupBy })} {...renderProps}>
        <>
          {multiple && renderCheckbox({ selected, option })}
          <span>{getOptionLabel ? getOptionLabel(option) : option}</span>
        </>
      </span>
    );
  };

  const renderStartAdornment = () =>
    startAdornment ? (
      <InputAdornment
        position="start"
        classes={{ root: cn(s.leftAdornment, classes?.adornment) }}
      >
        {startAdornment}
      </InputAdornment>
    ) : null;

  const renderEndAdornment = () =>
    endAdornment ? (
      <InputAdornment
        position="end"
        classes={{ root: cn(s.rightAdornment, classes?.adornment) }}
      >
        {endAdornment}
      </InputAdornment>
    ) : null;

  const shouldDisplayPlaceholder = shouldDisplay({
    disabled,
    multiple,
    controlledValue,
    displayPlaceholder,
  });

  const renderInput = params => (
    <TextField
      {...params}
      FormHelperTextProps={{
        classes: {
          root: s.helperText,
        },
      }}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <>
            {renderStartAdornment()}
            {params.InputProps.startAdornment}
          </>
        ),
        endAdornment: (
          <>
            {renderEndAdornment()}
            {params.InputProps.endAdornment}
          </>
        ),
        classes: {
          root: cn(s.inputRoot, autofill && s.autoFillRoot, {
            [s.inputDropdown]: !isSelect,
            [s.inputCompact]: compact,
            [s.titleText]: useTitleText,
          }),
          input: cn(s.input, autofill && s.inputAutofilled, {
            [s.inputDropdown]: !isSelect,
          }),
          ...inputClasses,
        },
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={{
        ...params.inputProps,
        readOnly: readOnlyInput,
        // use one-time-code to prevent autofill, off is not restrictive enough
        autoComplete: autofill ? 'on' : 'one-time-code',
        'data-testid': testId,
        'aria-label': inputAriaLabel,
      }}
      InputLabelProps={{
        shrink: true,
      }}
      label={!hideLabel ? label : undefined}
      variant="outlined"
      placeholder={shouldDisplayPlaceholder ? placeholder : null}
      error={error}
      helperText={helperText}
    />
  );

  const selectAll = () => {
    setControlledValue(options || items);
    onValueChange(null, options || items);
  };

  const selectNone = () => {
    setControlledValue([]);
    onValueChange(null, []);
  };

  const onScroll = ({ scrollOffset, scrollDirection }, ref) => {
    if (!isAsync) {
      return;
    }
    // Load next page when user scrolls to bottom 20px of the list
    // necessary to make the next page load when using keyboard navigation
    const bottomScrollThreshold = 20;
    if (
      Boolean(ref?.current) &&
      scrollOffset + ref.current.offsetHeight + bottomScrollThreshold >=
        ref.current.scrollHeight &&
      hasNextPage &&
      !isLoading
    ) {
      loadNextPage();
    }

    // Prevents list box from jumping to top after loading next page
    const jumpedToTopScrollThreshold = 60;
    if (
      scrollOffset < jumpedToTopScrollThreshold &&
      lastScrollRef.current > jumpedToTopScrollThreshold &&
      scrollDirection !== 'forward'
    ) {
      // eslint-disable-next-line no-param-reassign
      ref.current.scrollTop = lastScrollRef.current;
    } else {
      lastScrollRef.current = scrollOffset;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearchString = useCallback(
    debounce(newInputValue => setSearchString(newInputValue), debounceInterval),
    [],
  );

  useEffect(() => {
    debouncedSetSearchString('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledValue]);

  const onInputChange = (event, newInputValue, reason) => {
    if (charLimit && newInputValue?.length > charLimit) {
      return;
    }
    onSearchTextChange(newInputValue);
    setInputValue(newInputValue);
    if (isAsync && ['input', 'reset', 'clear'].includes(reason)) {
      debouncedSetSearchString(newInputValue);
    }
    if (multiple && reason === 'reset') {
      return;
    }
    if (autofill && reason === 'input') {
      onChange(newInputValue);
    }
  };

  const handleBlur = e => {
    if (isFunction(onBlur)) {
      onBlur(e.target.value);
      return;
    }
    const isCreatable =
      creatable && !isEmpty(inputValue) && isEmpty(controlledValue);
    if (isCreatable) {
      // If input loses focus, add current inputValue to onChange func
      onValueChange(e, inputValue);
    }

    setControlledOpen(false);
  };

  let autocompleteOptions = [];
  if (apiFuncUniqBy) {
    autocompleteOptions = uniqBy(
      [...ensureArray(additionalStaticOptions), ...items],
      apiFuncUniqBy,
    );
  } else if (isNonEmptyArray(options)) {
    autocompleteOptions = options;
  } else if (isNonEmptyArray(items)) {
    autocompleteOptions = [...ensureArray(additionalStaticOptions), ...items];
  }

  const handleClickAway = () => {
    /* Check if the text that the user has typed already matches one of the options.
     * If so, set that as the value so that we don't create a duplicate entry. */
    const matchingOption = ensureArray(autocompleteOptions).find(
      opt => opt?.name && value?.title && opt.name === value.title,
    );
    if (matchingOption) {
      setControlledValue(matchingOption);
    }
    setControlledOpen(false);
  };

  const onHighlightChange = async (_e, option) => {
    const listContainer = document?.querySelector(`.${listClassName.current}`);

    if (!listContainer || !option || multiple) {
      return;
    }

    const highlightedOptionIndex = autocompleteOptions
      .map(e => e?.title)
      .indexOf(option?.title);

    listContainer
      .querySelectorAll('.MuiAutocomplete-option')
      .forEach((el, index) => {
        if (index !== highlightedOptionIndex) {
          el.classList.remove('Mui-focused');
          el.classList.remove('Mui-focusVisible');
        }
      });
  };

  const renderTags = (values, getTagProps) =>
    values.map((option, index) => {
      const {
        confirmationText,
        confirmationPopoverCancelButtonText,
        confirmationButtonText,
        confirmationButtonColor,
      } = ensureObject(chipPopoverProps);
      const tagProps = getTagProps({ index });
      const isDisabled = getOptionDisabled
        ? getOptionDisabled(option, values)
        : disabledOptionRemoval || disabled;
      let optionToRender = option;
      const chipTooltipMessage = getChipTooltipMessage(option);

      if (getAutocompleteOptionLabel) {
        optionToRender = getAutocompleteOptionLabel(
          option,
          autocompleteOptions,
        );
      }
      const text = !isNil(optionToRender) && getOptionText(optionToRender);
      const showGlueText = index < values.length - 1 && glueText;
      const getOnDeleteParam = e => {
        if (chipPopoverProps) {
          setTagsOnDeleteFunc(() => () => tagProps.onDelete(e));
          return openPopover(e);
        }
        return isDisabled ? null : tagProps?.onDelete(e);
      };
      return !isNil(optionToRender) ? (
        <>
          <Tooltip placement="top" title={chipTooltipMessage}>
            <div className={s.chipContainer}>
              <Chip
                // eslint-disable-next-line react/no-array-index-key
                key={`${text}-${index}`}
                label={text}
                {...getTagProps({ index })}
                classes={{
                  rootClass: cn(
                    s.chip,
                    { [s.compactChip]: compact },
                    { [s.disabledChip]: isDisabled },
                  ),
                  rightComponentClass: cn(s.chipDelete, {
                    [s.chipDeleteDisabled]: isDisabled,
                  }),
                }}
                rightComponent={
                  !isDisabled ? <FontAwesomeIcon icon={faXmark} /> : <span />
                }
                disabled={isDisabled}
                onDelete={getOnDeleteParam}
                size={compact ? 'small' : 'medium'}
              />
            </div>
          </Tooltip>
          {showGlueText && (
            <Text color="gray" variant="meta">
              {glueText}
            </Text>
          )}
          <Popover
            id={popoverId}
            open={isPopoverOpen}
            anchorEl={anchorEl}
            onClose={closePopover}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            classes={{
              root: s.popoverRoot,
              paper: s.popoverPaper,
            }}
          >
            <Text variant="bodySm">{confirmationText}</Text>
            <Flexbox
              alignItems="center"
              justifyContent="space-between"
              mt={1.5}
            >
              <Button
                label={confirmationPopoverCancelButtonText}
                color="gray"
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  closePopover();
                }}
              />
              <Button
                label={confirmationButtonText}
                color={confirmationButtonColor}
                variant="outlined"
                size="small"
                onClick={() => {
                  tagsOnDeleteFunc();
                  closePopover();
                }}
              />
            </Flexbox>
          </Popover>
        </>
      ) : null;
    });

  if (isFunction(getOptionNodeLabel)) {
    return (
      <SelectWithNodeLabel
        onChange={onChange}
        value={value}
        options={options}
        getOptionNodeLabel={getOptionNodeLabel}
        testId={testId}
        className={classes?.container}
        disabled={disabled}
      />
    );
  }

  return (
    <div className={cn(classes?.container, s.container)}>
      {!isLabelOutlined && <span className={s.label}>{label}</span>}

      <ClickAwayListener onClickAway={handleClickAway}>
        <div className={s.fullWidth}>
          <Autocomplete
            id={id}
            fullWidth
            disableListWrap
            openOnFocus={openOnFocus}
            selectOnFocus={false}
            handleHomeEndKeys
            value={controlledValue}
            onChange={onValueChange}
            freeSolo={isSelect}
            options={autocompleteOptions}
            filterOptions={filterOptions}
            getOptionLabel={getOptionText}
            renderOption={renderOptionRow}
            getOptionDisabled={
              getOptionDisabled || (option => option?.isLoadingIndicator)
            }
            renderInput={renderInput}
            disabled={disabled}
            disableClearable={disableClearable}
            groupBy={groupBy}
            renderTags={renderTags}
            renderGroup={renderGroup}
            multiple={multiple}
            ListboxComponent={listBox}
            ListboxProps={{
              onScroll,
              style: {
                position: 'relative',
                height: 'auto',
              },
              menuEl: hasSelectMenu && !autoSuggestHighlight && (
                <Menu onSelectAll={selectAll} onSelectNone={selectNone} />
              ),
              // https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
              role: 'listbox',
              // TODO when transition it to @schoolinks/ui-components, move these to theming
              sx: {
                '& .MuiAutocomplete-option.Mui-focused': {
                  backgroundColor: 'var(--color-blue-bg) !important',
                },
              },
            }}
            onHighlightChange={onHighlightChange}
            onInputChange={onInputChange}
            classes={{
              popper: classes?.popper,
              option: cn(s.optionContainer, classes?.optionContainer),
              listbox: cn(s.options, listClassName.current, classes?.listbox),
              tag: s.limitTags,
              ...classes,
            }}
            sx={{ width: width ? `${width}px !important` : undefined }}
            disableCloseOnSelect={multiple && !closeOnSelect}
            inputValue={inputValue}
            limitTags={limitTags}
            getLimitTagsText={more => `+ ${more} more`}
            isOptionEqualToValue={getOptionSelected}
            open={
              isFunction(showOptionsOnCondition)
                ? showOptionsOnCondition(inputValue) && isOpen
                : isOpen
            }
            onOpen={() => {
              setControlledOpen(true);
              onOpen({ value: controlledValue });
            }}
            onClose={(obj, reason) => {
              if (reason !== 'blur') {
                setControlledOpen(false);
              }
              onClose({ innerText: obj?.target?.innerText });
            }}
            disablePortal={disablePortal}
            onBlur={handleBlur}
            autoHighlight={autoHighlight}
            noOptionsText={noOptionsText}
          />
        </div>
      </ClickAwayListener>
    </div>
  );
};

Menu.propTypes = {
  onSelectAll: func.isRequired,
  onSelectNone: func.isRequired,
};

Select.propTypes = {
  apiFunc: func,
  apiFuncName: string,
  apiFuncOptions: shape({}),
  additionalStaticOptions: arrayOf(shape({})),
  apiFuncUniqBy: func,
  autofill: bool,
  autoSuggestHighlight: bool,
  charLimit: number,
  classes: shape({
    container: node,
    adornment: string,
    optionRow: string,
  }),
  closeOnSelect: bool,
  compact: bool,
  creatable: bool,
  addOptionPromptActionFn: func,
  disableClearable: bool,
  disabled: bool,
  disablePortal: bool,
  endAdornment: shape({}),
  error: bool,
  getAutocompleteOptionLabel: func,
  getOptionLabel: func,
  getOptionNodeLabel: func,
  getOptionSelected: func,
  glueText: string,
  groupBy: func,
  hasSelectMenu: bool,
  helperText: string,
  id: string,
  inputClasses: shape({}),
  isObjectValue: bool,
  label: string,
  labelVariant: oneOf(['outlined', 'standard']),
  limitTags: number,
  listboxComponent: node,
  multiple: bool,
  onBlur: func,
  getOptionDisabled: func,
  showOptionsOnCondition: func,
  onChange: func.isRequired,
  onSearchTextChange: func,
  open: bool,
  openOnFocus: bool,
  options: arrayOf(shape({})),
  placeholder: string,
  readOnlyInput: bool,
  renderOption: func,
  requireSelectedOption: bool,
  /* Based off of this SO answer: https://stackoverflow.com/a/68506363 */
  showAllOptions: bool,
  startAdornment: shape({}),
  testId: string,
  inputAriaLabel: string,
  useTitleText: bool,
  value: oneOfType([string, shape({}), arrayOf(shape({}))]).isRequired,
  variant: oneOf(['select', 'dropdown']),
  width: number,
  setValueAfterCreate: bool,
  optionVariableName: string,
  displayPlaceholder: bool,
  autoHighlight: bool,
  onOpen: func,
  onClose: func,
  noOptionsText: oneOfType([string, node]),
  chipPopoverProps: shape({}),
  getChipTooltipMessage: func,
  showApplyButton: bool,
  onApply: func,
  onCancel: func,
  onClearAllValues: func,
  useVariableSizeList: bool,
};

Select.defaultProps = {
  apiFunc: null,
  apiFuncName: '',
  apiFuncOptions: {},
  additionalStaticOptions: [],
  apiFuncUniqBy: null,
  autofill: false,
  autoSuggestHighlight: true,
  charLimit: undefined,
  classes: {},
  closeOnSelect: false,
  compact: false,
  creatable: false,
  addOptionPromptActionFn: null,
  disableClearable: false,
  disabled: false,
  disablePortal: false,
  endAdornment: null,
  error: false,
  getAutocompleteOptionLabel: null,
  getOptionLabel: null,
  showOptionsOnCondition: undefined,
  getOptionNodeLabel: null,
  getOptionSelected: noop,
  glueText: undefined,
  groupBy: null,
  hasSelectMenu: false,
  helperText: null,
  id: null,
  inputClasses: {},
  isObjectValue: false,
  label: '',
  labelVariant: 'outlined',
  limitTags: 6,
  listboxComponent: null,
  multiple: false,
  onBlur: () => {},
  getOptionDisabled: undefined,
  onSearchTextChange: noop,
  open: undefined,
  openOnFocus: true,
  options: null,
  placeholder: '',
  readOnlyInput: false,
  renderOption: null,
  requireSelectedOption: false,
  showAllOptions: false,
  startAdornment: null,
  testId: null,
  inputAriaLabel: '',
  useTitleText: false,
  variant: 'select',
  width: undefined,
  setValueAfterCreate: false,
  optionVariableName: 'name',
  displayPlaceholder: false,
  autoHighlight: false,
  onOpen: noop,
  onClose: noop,
  noOptionsText: undefined,
  chipPopoverProps: undefined,
  getChipTooltipMessage: noop,
  showApplyButton: false,
  onApply: noop,
  onCancel: noop,
  onClearAllValues: noop,
  useVariableSizeList: false,
};

export default Select;
